import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Plus, X } from "lucide-react";
import { Dialog } from "@/components/ui/Dialog";
import { useImportCodepushApp } from "../hooks/useImportCodepushApp";
import { ImportedKeyList } from "./ImportedKeyList";
import {
  DEPLOYMENT_KEY_MAX_LENGTH,
  DEPLOYMENT_KEY_MIN_LENGTH,
  blankKeyRows,
  isComplete,
  validateAppName,
  validateRows,
  type BlankKeyRow,
  type ImportRow,
  type RowFailureKind,
  type RowOutcome,
} from "../lib/codepush-import";

const MIGRATION_DOCS_URL = "https://docs.aetherpush.com/migration/codepush/";
const LEGACY_KEY_COMMAND = "code-push deployment ls <app> -k";
const NAME_FIELD_KINDS: ReadonlySet<RowFailureKind> = new Set(["nameTaken", "keyOnEarlierName"]);
const ONLY_ROW_VARIANTS = {
  nameTaken: "nameTakenOnlyRow",
  keyMismatch: "keyMismatchOnlyRow",
  earlierKeyStored: "earlierKeyStoredOnlyRow",
} as const satisfies Partial<Record<RowFailureKind, string>>;

function hasOnlyRowVariant(kind: RowFailureKind): kind is keyof typeof ONLY_ROW_VARIANTS {
  return kind in ONLY_ROW_VARIANTS;
}

function focusesNameField(row: ImportRow, kind: RowFailureKind): boolean {
  if (NAME_FIELD_KINDS.has(kind)) return true;
  return !row.key.trim() && kind !== "earlierKeyStored";
}
const INPUT_CLASS =
  "h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo disabled:cursor-not-allowed disabled:opacity-50 hover:border-border-light";

interface ImportCodepushDialogProps {
  open: boolean;
  onClose: () => void;
  onViewApp?: (appName: string) => void;
}

interface CompletedImport {
  appName: string;
  existed: boolean;
  deployments: string[];
  generatedKeys: BlankKeyRow[];
  foundKeys: BlankKeyRow[];
}

function newRow(name: string): ImportRow {
  return { id: crypto.randomUUID(), name, key: "" };
}

function defaultRows(): ImportRow[] {
  return [newRow("Production"), newRow("Staging")];
}

export function ImportCodepushDialog({ open, onClose, onViewApp }: ImportCodepushDialogProps) {
  const { t } = useTranslation();
  const [appName, setAppName] = useState("");
  const [rows, setRows] = useState<ImportRow[]>(defaultRows);
  const [showIssues, setShowIssues] = useState(false);
  const [completed, setCompleted] = useState<CompletedImport | null>(null);
  const { progress, isRunning, submit, reset, clearAppFailure, clearRowFailure } =
    useImportCodepushApp();
  const appNameRef = useRef<HTMLInputElement>(null);
  const keyRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const nameRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const wasRunning = useRef(false);
  const focusAppNameNext = useRef(false);
  const adoptionConfirmed = useRef(false);
  const completedHeadingRef = useRef<HTMLHeadingElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  const lockedIds = new Set(
    rows.filter((row) => progress.rows[row.id]?.phase === "done").map((row) => row.id),
  );
  const appIssue = validateAppName(appName);
  const rowIssues = validateRows(rows, lockedIds);
  const appReady = progress.app.phase === "ready";
  const failedRows = rows.filter((row) => progress.rows[row.id]?.phase === "failed").length;
  const doneRows = lockedIds.size;

  useEffect(() => {
    if (!wasRunning.current || isRunning) {
      wasRunning.current = isRunning;
      return;
    }
    wasRunning.current = false;

    if (progress.app.phase === "failed") {
      appNameRef.current?.focus();
      return;
    }
    const firstFailed = rows.find((row) => progress.rows[row.id]?.phase === "failed");
    if (!firstFailed) return;
    const outcome = progress.rows[firstFailed.id];
    const target =
      outcome?.phase === "failed" && focusesNameField(firstFailed, outcome.kind)
        ? nameRefs.current[firstFailed.id]
        : keyRefs.current[firstFailed.id];
    target?.focus();
  }, [isRunning, progress, rows]);

  useEffect(() => {
    if (!focusAppNameNext.current || appReady || isRunning) return;
    focusAppNameNext.current = false;
    appNameRef.current?.focus();
  }, [appReady, isRunning]);

  useEffect(() => {
    if (completed) completedHeadingRef.current?.focus();
  }, [completed]);

  const startOver = () => {
    const fresh = defaultRows();
    setAppName("");
    setRows(fresh);
    setShowIssues(false);
    reset(fresh);
    adoptionConfirmed.current = false;
  };

  const run = async (useExistingApp: boolean) => {
    if (isRunning) return;
    setShowIssues(true);
    if (appIssue || Object.keys(rowIssues).length > 0) return;

    adoptionConfirmed.current = useExistingApp;
    wasRunning.current = true;
    submitRef.current?.focus();
    const result = await submit({ appName: appName.trim(), rows, useExistingApp });
    if (isComplete(result, rows)) {
      const blank = blankKeyRows(result, rows);
      setCompleted({
        appName: appName.trim(),
        existed: result.app.phase === "ready" && result.app.existed,
        deployments: rows.map((row) => row.name.trim()),
        generatedKeys: blank.generated,
        foundKeys: blank.found,
      });
    }
  };

  const importAnother = () => {
    if (!completed) return;
    const fresh = completed.deployments.map((name) => newRow(name));
    setAppName("");
    setRows(fresh);
    setShowIssues(false);
    setCompleted(null);
    reset(fresh);
    adoptionConfirmed.current = false;
    focusAppNameNext.current = true;
  };

  const closeCompleted = () => {
    setCompleted(null);
    startOver();
    onClose();
  };

  const viewApp = () => {
    if (!completed) return;
    const { appName: createdName } = completed;
    closeCompleted();
    onViewApp?.(createdName);
  };

  const handleClose = () => {
    if (isRunning) return;
    if (completed) {
      closeCompleted();
      return;
    }
    setShowIssues(false);
    onClose();
  };

  const changeAppName = (value: string) => {
    setAppName(value);
    clearAppFailure();
    adoptionConfirmed.current = false;
  };

  const updateRow = (id: string, patch: Partial<ImportRow>) => {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    clearRowFailure(id);
  };

  const restoreKey = (id: string, storedKey: string) => {
    updateRow(id, { key: storedKey });
    keyRefs.current[id]?.focus();
  };

  const removeRow = (id: string) => {
    setRows((current) => current.filter((row) => row.id !== id));
    delete keyRefs.current[id];
    delete nameRefs.current[id];
  };

  const rowError = (row: ImportRow): string | null => {
    const issue = rowIssues[row.id];
    if (showIssues && issue) {
      return t(`apps.import.issue.${issue}`, {
        min: DEPLOYMENT_KEY_MIN_LENGTH,
        max: DEPLOYMENT_KEY_MAX_LENGTH,
      });
    }
    const outcome: RowOutcome | undefined = progress.rows[row.id];
    if (outcome?.phase !== "failed") return null;
    if (outcome.message) return outcome.message;
    const kind =
      rows.length === 1 && hasOnlyRowVariant(outcome.kind)
        ? ONLY_ROW_VARIANTS[outcome.kind]
        : outcome.kind;
    return t(`apps.import.rowError.${kind}`, {
      name: row.name.trim(),
      earlierName: outcome.earlierName,
    });
  };

  const appError = (): string | null => {
    if (showIssues && appIssue) return t(`apps.import.appIssue.${appIssue}`);
    const app = progress.app;
    if (app.phase !== "failed" || app.kind === "nameTaken") return null;
    if (app.message) return app.message;
    return t(`apps.import.appError.${app.kind}`);
  };

  const appNameTaken = progress.app.phase === "failed" && progress.app.kind === "nameTaken";
  const appErrorText = appError();
  const partialText =
    failedRows > 0 ? t("apps.import.partial", { done: doneRows, total: rows.length }) : null;
  const statusText = isRunning ? t("apps.import.importing") : partialText;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t("apps.import.title")}
      description={completed ? undefined : t("apps.import.description")}
      size="lg"
    >
      {completed ? (
        <div>
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-success/10 text-success">
              <Check size={20} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h3
                ref={completedHeadingRef}
                tabIndex={-1}
                className="break-words text-base font-semibold text-heading focus:outline-none"
              >
                {t("apps.import.completedTitle", { name: completed.appName })}
              </h3>
              <p className="mt-1 break-words text-sm text-muted">
                {t(
                  completed.existed
                    ? "apps.import.completedDeploymentsExisting"
                    : "apps.import.completedDeployments",
                  {
                    count: completed.deployments.length,
                    names: completed.deployments.join(", "),
                  },
                )}
              </p>
              <ImportedKeyList
                appName={completed.appName}
                generated={completed.generatedKeys}
                found={completed.foundKeys}
              />
            </div>
          </div>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={closeCompleted}
              className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
            >
              {t("apps.import.completedClose")}
            </button>
            {onViewApp && (
              <button
                type="button"
                onClick={viewApp}
                className="max-w-full truncate rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
              >
                {t("apps.import.viewApp", { name: completed.appName })}
              </button>
            )}
            <button
              type="button"
              onClick={importAnother}
              className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright"
            >
              {t("apps.import.importAnother")}
            </button>
          </div>
        </div>
      ) : (
        <form
          noValidate
          onSubmit={(event) => {
            event.preventDefault();
            void run(adoptionConfirmed.current);
          }}
        >
          <p role="status" className="sr-only">
            {statusText}
          </p>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="import-app-name" className="text-sm font-medium text-subtle">
              {t("apps.appNameLabel")}
            </label>
            <input
              id="import-app-name"
              ref={appNameRef}
              type="text"
              value={appName}
              onChange={(event) => changeAppName(event.target.value)}
              placeholder={t("apps.appNamePlaceholder")}
              data-autofocus
              disabled={isRunning || appReady}
              aria-invalid={appErrorText || appNameTaken ? true : undefined}
              aria-describedby={
                appErrorText ? "import-app-error" : appNameTaken ? "import-app-conflict" : undefined
              }
              className={INPUT_CLASS}
            />
            {appReady && !isRunning && (
              <button
                type="button"
                onClick={() => {
                  focusAppNameNext.current = true;
                  startOver();
                }}
                className="self-start rounded-lg border border-border bg-surface px-3.5 py-2 text-xs font-medium text-muted transition-colors hover:border-border-light hover:text-heading"
              >
                {t("apps.import.startOver")}
              </button>
            )}
            {appErrorText && (
              <p id="import-app-error" role="alert" className="text-xs text-danger">
                {appErrorText}
              </p>
            )}
            {appNameTaken && (
              <div
                id="import-app-conflict"
                role="alert"
                className="flex flex-wrap items-center gap-2 text-xs text-danger"
              >
                <span>{t("apps.import.appExists", { name: appName.trim() })}</span>
                <button
                  type="button"
                  onClick={() => void run(true)}
                  disabled={isRunning}
                  className="rounded-md border border-border px-2 py-1 font-medium text-muted transition-colors hover:border-border-light hover:text-heading disabled:opacity-50"
                >
                  {t("apps.import.useExistingApp")}
                </button>
              </div>
            )}
          </div>

          <fieldset className="mt-5 border-0 p-0">
            <legend className="text-sm font-medium text-subtle">
              {t("apps.import.deploymentsLegend")}
            </legend>
            <p className="mt-1 text-xs text-muted">{t("apps.import.keyHelp")}</p>
            <p className="mt-1 text-xs text-muted">
              <code className="rounded bg-elevated px-1.5 py-0.5 font-mono">
                {LEGACY_KEY_COMMAND}
              </code>{" "}
              <a
                href={MIGRATION_DOCS_URL}
                target="_blank"
                rel="noreferrer"
                className="text-indigo underline underline-offset-2 hover:text-indigo-bright"
              >
                {t("apps.import.docsLink")}
              </a>
            </p>

            <ul className="mt-3 flex flex-col gap-3">
              {rows.map((row) => {
                const rowDone = lockedIds.has(row.id);
                const error = rowError(row);
                const errorId = `import-row-error-${row.id}`;
                const described = error ? errorId : undefined;
                const outcome = progress.rows[row.id];
                const showsOutcome = !(showIssues && rowIssues[row.id]);
                const earlierKey =
                  showsOutcome && outcome?.phase === "failed" && outcome.kind === "earlierKeyStored"
                    ? outcome.storedKey
                    : undefined;

                return (
                  <li key={row.id} className="flex flex-col gap-1.5">
                    <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-start">
                      <div className="min-w-0 sm:flex-1">
                        <label htmlFor={`import-name-${row.id}`} className="sr-only">
                          {t("deployments.deploymentNameLabel")}
                        </label>
                        <input
                          id={`import-name-${row.id}`}
                          ref={(element) => {
                            nameRefs.current[row.id] = element;
                          }}
                          type="text"
                          value={row.name}
                          onChange={(event) => updateRow(row.id, { name: event.target.value })}
                          placeholder={t("deployments.deploymentNamePlaceholder")}
                          disabled={isRunning || rowDone}
                          aria-invalid={error ? true : undefined}
                          aria-describedby={described}
                          className={INPUT_CLASS}
                        />
                      </div>
                      <div className="flex items-start gap-2 sm:flex-[1.6]">
                        <div className="min-w-0 flex-1">
                          <label htmlFor={`import-key-${row.id}`} className="sr-only">
                            {t("apps.import.keyLabel")}
                          </label>
                          <input
                            id={`import-key-${row.id}`}
                            ref={(element) => {
                              keyRefs.current[row.id] = element;
                            }}
                            type="text"
                            value={row.key}
                            onChange={(event) => updateRow(row.id, { key: event.target.value })}
                            placeholder={t("apps.import.keyPlaceholder")}
                            disabled={isRunning || rowDone}
                            spellCheck={false}
                            autoComplete="off"
                            aria-invalid={error ? true : undefined}
                            aria-describedby={described}
                            className={`${INPUT_CLASS} font-mono`}
                          />
                        </div>
                        {rowDone ? (
                          <span className="flex h-11 w-8 shrink-0 items-center justify-center text-success">
                            <Check size={16} aria-hidden="true" />
                            <span className="sr-only">{t("apps.import.rowCreated")}</span>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => removeRow(row.id)}
                            disabled={isRunning || rows.length === 1}
                            aria-label={
                              row.name.trim()
                                ? t("apps.import.removeDeployment", { name: row.name.trim() })
                                : t("apps.import.removeDeploymentUnnamed")
                            }
                            className="flex h-11 w-8 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:text-heading disabled:opacity-30"
                          >
                            <X size={16} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </div>
                    {error && !earlierKey && (
                      <p id={errorId} role="alert" className="break-words text-xs text-danger">
                        {error}
                      </p>
                    )}
                    {error && earlierKey && (
                      <div
                        id={errorId}
                        role="alert"
                        className="flex flex-wrap items-center gap-2 text-xs text-danger"
                      >
                        <span className="min-w-0 break-words">{error}</span>
                        <button
                          type="button"
                          onClick={() => restoreKey(row.id, earlierKey)}
                          disabled={isRunning}
                          className="rounded-md border border-border px-2 py-1 font-medium text-muted transition-colors hover:border-border-light hover:text-heading disabled:opacity-50"
                        >
                          {t("apps.import.restoreEarlierKey")}
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            <button
              type="button"
              onClick={() => setRows((current) => [...current, newRow("")])}
              disabled={isRunning}
              className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium text-muted transition-colors hover:border-border-light hover:text-heading disabled:opacity-50"
            >
              <Plus size={14} aria-hidden="true" />
              {t("apps.import.addDeployment")}
            </button>
          </fieldset>

          {partialText && <p className="mt-4 text-xs text-muted">{partialText}</p>}

          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              disabled={isRunning}
              className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading disabled:opacity-50"
            >
              {t("common.cancel")}
            </button>
            <button
              ref={submitRef}
              type="submit"
              aria-disabled={isRunning || undefined}
              className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright aria-disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:hover:bg-indigo"
            >
              {isRunning
                ? t("apps.import.importing")
                : failedRows > 0
                  ? t("apps.import.retry")
                  : t("apps.import.submit")}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
